import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAuth } from '../../services/adminAuth';
import { Lock, Mail, Loader2, AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminAuth.login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0f1117] text-white px-4"
      style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(59,130,246,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(139,92,246,0.10), transparent 40%)' }}
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-4">
            <Lock className="w-6 h-6 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <span className="text-blue-400">Student</span> Store It
          </h1>
          <p className="text-sm text-gray-400 mt-1">Admin Portal Sign In</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#161822] border border-[#1e2030] rounded-2xl p-6 space-y-4"
          data-testid="admin-login-form"
        >
          {error && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
              data-testid="admin-login-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-400 font-medium block mb-1.5">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@studentstoreit.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#0f1117] border border-[#1e2030] text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40"
                data-testid="admin-login-email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 font-medium block mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#0f1117] border border-[#1e2030] text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40"
                data-testid="admin-login-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Signing in…' : 'Sign In'}
          </button>

          <p className="text-xs text-gray-500 text-center pt-2">
            Authorized personnel only. All access is logged.
          </p>
        </form>
      </div>
    </div>
  );
}
